import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, Card, CardContent, Chip, Skeleton,
  Alert, alpha, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Select, MenuItem, FormControl,
  InputLabel, Pagination, LinearProgress, Tooltip,
} from '@mui/material';
import {
  TrendingUp as WinIcon,
  TrendingDown as LossIcon,
  Timer as ExpiredIcon,
  CheckCircleOutline as ActiveIcon,
  Assessment as ChartIcon,
  FiberManualRecord as DotIcon,
  BoltOutlined as StrengthIcon,
  SwapVert as RRIcon,
  SignalCellularAlt as SignalIcon,
  GradeOutlined as ScoreIcon,
  BalanceOutlined as RatioIcon,
  LayersOutlined as StrategyIcon,
  EmojiEventsOutlined as WinRateIcon,
  ShowChartOutlined as ReturnIcon,
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, LineChart, Line, Legend,
} from 'recharts';
import { legendaryAPI } from '../../services/api';

const STRATEGY_COLORS = {
  ONEIL: '#6366f1',
  MINERVINI: '#8b5cf6',
  WEINSTEIN: '#14b8a6',
  TURTLE: '#f59e0b',
  LIVERMORE: '#f43f5e',
};

const STRATEGY_LABELS = {
  ONEIL: "O'Neil",
  MINERVINI: 'Minervini',
  WEINSTEIN: 'Weinstein',
  TURTLE: 'Turtle',
  LIVERMORE: 'Livermore',
};

// Color-code reason chips by category (shared with SignalTable)
const REASON_CHIP_STYLES = {
  breakout: { bg: '#ede9fe', color: '#7c3aed' },
  pivot: { bg: '#ede9fe', color: '#7c3aed' },
  volume: { bg: '#dbeafe', color: '#2563eb' },
  ma: { bg: '#d1fae5', color: '#059669' },
  sma: { bg: '#d1fae5', color: '#059669' },
  trend: { bg: '#d1fae5', color: '#059669' },
  strong: { bg: '#fef3c7', color: '#b45309' },
  close: { bg: '#fef3c7', color: '#b45309' },
  donchian: { bg: '#ffedd5', color: '#c2410c' },
  channel: { bg: '#ffedd5', color: '#c2410c' },
  atr: { bg: '#fce7f3', color: '#be185d' },
  risk: { bg: '#fce7f3', color: '#be185d' },
  rs: { bg: '#e0f2fe', color: '#0369a1' },
  zone: { bg: '#fef9c3', color: '#a16207' },
  midpoint: { bg: '#f0fdf4', color: '#15803d' },
};

const getReasonChipStyle = (reason) => {
  if (!reason) return { bg: '#f1f5f9', color: '#475569' };
  const lower = reason.toLowerCase();
  for (const [key, style] of Object.entries(REASON_CHIP_STYLES)) {
    if (lower.includes(key)) return style;
  }
  return { bg: '#f1f5f9', color: '#475569' };
};

function reasonLabel(r, t) {
  const key = `legendary.reasons.${r}`;
  const translated = t(key, '');
  if (translated && translated !== key) return translated;
  return r.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

// ── Section Header ───────────────────────────────────────
function SectionHeader({ title, subtitle, right, color }) {
  const accent = color || '#6366f1';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 4, height: 24, borderRadius: 2, bgcolor: accent }} />
        <Box>
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1e293b', fontSize: '0.95rem', lineHeight: 1.3 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.72rem' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      {right}
    </Box>
  );
}

// ── Metric Card ──────────────────────────────────────────
function MetricCard({ icon, label, value, sub, color, gradFrom, gradTo }) {
  const c = color || '#6366f1';
  return (
    <Card elevation={0} sx={{
      border: '1px solid', borderColor: alpha(c, 0.15), borderRadius: 2,
      flex: 1, minWidth: 0, position: 'relative', overflow: 'hidden',
      '&:hover': { borderColor: alpha(c, 0.3), transform: 'translateY(-1px)', boxShadow: `0 4px 16px ${alpha(c, 0.08)}` },
      transition: 'all 0.2s ease',
    }}>
      {/* Accent top bar */}
      <Box sx={{ height: 3, bgcolor: c }} />
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="caption" sx={{
              color: '#64748b', fontWeight: 600, textTransform: 'uppercase',
              fontSize: '0.6rem', letterSpacing: '0.06em', display: 'block', mb: 0.5,
            }}>
              {label}
            </Typography>
            <Typography variant="h5" sx={{
              fontWeight: 800, fontFamily: 'monospace', lineHeight: 1.1,
              color: c, fontSize: '1.3rem',
            }}>
              {value}
            </Typography>
            {sub && (
              <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.65rem', mt: 0.3, display: 'block' }}>
                {sub}
              </Typography>
            )}
          </Box>
          <Box sx={{
            width: 36, height: 36, borderRadius: 2,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: alpha(c, 0.08), color: c, flexShrink: 0,
          }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

// ── Overview Banner ──────────────────────────────────────
function OverviewBanner({ summary, t }) {
  const hasClosed = summary?.total_closed > 0;
  const activeCount = summary?.total_signals - (summary?.total_closed || 0);
  const stratCount = summary?.strategies?.filter(s => s.total_signals > 0).length || 0;

  return (
    <Box sx={{ display: 'flex', gap: 2, mb: 3, width: '100%' }}>
      <MetricCard
        icon={<SignalIcon sx={{ fontSize: 20 }} />}
        label={t('legendary.accuracy.totalSignals', 'Total Signals')}
        value={summary?.total_signals || 0}
        sub={t('legendary.accuracy.active', '{{count}} active', { count: activeCount })}
        color="#6366f1"
        gradFrom="#6366f1" gradTo="#a78bfa"
      />
      {hasClosed ? (
        <>
          <MetricCard
            icon={<WinRateIcon sx={{ fontSize: 20 }} />}
            label={t('legendary.accuracy.metric.winRate', 'Win Rate')}
            value={`${summary?.overall_win_rate || 0}%`}
            color={summary?.overall_win_rate >= 50 ? '#10b981' : '#ef4444'}
            gradFrom={summary?.overall_win_rate >= 50 ? '#10b981' : '#ef4444'}
            gradTo={summary?.overall_win_rate >= 50 ? '#6ee7b7' : '#fca5a5'}
          />
          <MetricCard
            icon={<ReturnIcon sx={{ fontSize: 20 }} />}
            label={t('legendary.accuracy.metric.avgReturn', 'Avg Return')}
            value={`${summary?.overall_avg_return > 0 ? '+' : ''}${summary?.overall_avg_return || 0}%`}
            color={summary?.overall_avg_return > 0 ? '#10b981' : '#ef4444'}
            gradFrom={summary?.overall_avg_return > 0 ? '#10b981' : '#ef4444'}
            gradTo={summary?.overall_avg_return > 0 ? '#6ee7b7' : '#fca5a5'}
          />
        </>
      ) : null}
      <MetricCard
        icon={<ScoreIcon sx={{ fontSize: 20 }} />}
        label={t('legendary.accuracy.metric.avgScore', 'Avg Score')}
        value={summary?.overall_avg_score ? summary.overall_avg_score : '—'}
        sub={t('legendary.accuracy.qualityScore', 'quality score')}
        color={summary?.overall_avg_score >= 85 ? '#10b981' : '#f59e0b'}
        gradFrom={summary?.overall_avg_score >= 85 ? '#10b981' : '#f59e0b'}
        gradTo={summary?.overall_avg_score >= 85 ? '#6ee7b7' : '#fcd34d'}
      />
      <MetricCard
        icon={<RatioIcon sx={{ fontSize: 20 }} />}
        label={t('legendary.consensus.avgRR', 'Avg R:R')}
        value={summary?.overall_avg_risk_reward || '—'}
        sub="risk / reward"
        color={summary?.overall_avg_risk_reward >= 1.5 ? '#0ea5e9' : '#64748b'}
        gradFrom="#0ea5e9" gradTo="#7dd3fc"
      />
      <MetricCard
        icon={<StrategyIcon sx={{ fontSize: 20 }} />}
        label={t('legendary.accuracy.strategies', 'Strategies')}
        value={`${stratCount}/5`}
        sub={t('legendary.accuracy.active', 'active')}
        color="#8b5cf6"
        gradFrom="#8b5cf6" gradTo="#c4b5fd"
      />
    </Box>
  );
}

// ── Strategy Card (richer info) ──────────────────────────
function StrategyCard({ data }) {
  if (!data) return null;
  const color = STRATEGY_COLORS[data.strategy] || '#667eea';
  const label = STRATEGY_LABELS[data.strategy] || data.strategy;
  const total = data.wins + data.losses + data.expired;
  const hasClosed = total > 0;
  const scoreVal = data.avg_score || 0;
  const scorePct = Math.min(scoreVal, 100);
  const scoreColor = scoreVal >= 90 ? '#10b981' : scoreVal >= 80 ? '#3b82f6' : scoreVal >= 70 ? '#f59e0b' : '#94a3b8';

  // Metrics to display
  const metrics = hasClosed
    ? [
        { label: 'Signals', value: data.total_signals, color: '#475569' },
        { label: 'Active', value: data.active_signals, color: '#3b82f6' },
        { label: 'Win Rate', value: `${data.win_rate}%`, color: data.win_rate >= 50 ? '#10b981' : '#ef4444', bold: true },
        { label: 'Avg Ret', value: `${data.avg_return > 0 ? '+' : ''}${data.avg_return}%`, color: data.avg_return > 0 ? '#10b981' : '#ef4444', bold: true },
      ]
    : [
        { label: 'Signals', value: data.total_signals, color: '#475569' },
        { label: 'Active', value: data.active_signals, color: '#3b82f6' },
        { label: 'R:R', value: data.avg_risk_reward || '—', color: '#6366f1', bold: true },
        { label: 'Strong', value: data.strong_count, color: '#10b981' },
      ];

  return (
    <Card elevation={0} sx={{
      borderRadius: 2, flex: '1 1 0', minWidth: 260, position: 'relative', overflow: 'hidden',
      border: '1px solid', borderColor: alpha(color, 0.12),
      '&:hover': {
        borderColor: alpha(color, 0.3),
        boxShadow: `0 6px 20px ${alpha(color, 0.10)}`,
        transform: 'translateY(-2px)',
      },
      transition: 'all 0.25s ease',
    }}>
      {/* Colored left accent */}
      <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, bgcolor: color }} />
      <CardContent sx={{ p: 0, pl: 0.5, '&:last-child': { pb: 0 } }}>
        {/* Header row */}
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2, pt: 1.5, pb: 1,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{
              width: 28, height: 28, borderRadius: 1.5, display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              bgcolor: alpha(color, 0.10), color: color,
            }}>
              <ChartIcon sx={{ fontSize: 16 }} />
            </Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.9rem', color: '#1e293b' }}>
              {label}
            </Typography>
          </Box>
          {/* Score badge */}
          {data.avg_score != null && (
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 0.5,
              px: 1, py: 0.3, borderRadius: 1.5,
              bgcolor: alpha(scoreColor, 0.08),
              border: `1px solid ${alpha(scoreColor, 0.15)}`,
            }}>
              <ScoreIcon sx={{ fontSize: 13, color: scoreColor }} />
              <Typography variant="caption" sx={{
                fontWeight: 800, fontFamily: 'monospace', fontSize: '0.78rem', color: scoreColor,
              }}>
                {data.avg_score}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Description — truncated to 1 line */}
        {data.description && (
          <Typography variant="caption" sx={{
            color: '#64748b', fontSize: '0.66rem', display: 'block', px: 2, mb: 1,
            lineHeight: 1.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {data.description}
          </Typography>
        )}

        {/* Score quality bar */}
        <Box sx={{ px: 2, mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ flex: 1, height: 5, borderRadius: 3, overflow: 'hidden', bgcolor: '#f1f5f9' }}>
              {hasClosed ? (
                <Box sx={{ display: 'flex', height: '100%' }}>
                  {data.wins > 0 && <Box sx={{ width: `${(data.wins / total) * 100}%`, bgcolor: '#10b981' }} />}
                  {data.losses > 0 && <Box sx={{ width: `${(data.losses / total) * 100}%`, bgcolor: '#ef4444' }} />}
                  {data.expired > 0 && <Box sx={{ width: `${(data.expired / total) * 100}%`, bgcolor: '#cbd5e1' }} />}
                </Box>
              ) : (
                <Box sx={{ width: `${scorePct}%`, height: '100%', borderRadius: 3, bgcolor: scoreColor, transition: 'width 0.5s ease' }} />
              )}
            </Box>
            <Typography variant="caption" sx={{ fontSize: '0.55rem', color: '#94a3b8', fontWeight: 600, flexShrink: 0 }}>
              {hasClosed ? `${data.win_rate}% WR` : `${scorePct} pts`}
            </Typography>
          </Box>
        </Box>

        {/* Metrics row — bordered top, full width */}
        <Box sx={{
          display: 'flex', borderTop: '1px solid #f1f5f9',
        }}>
          {metrics.map((m, i) => (
            <Box key={i} sx={{
              flex: 1, textAlign: 'center', py: 1,
              borderRight: i < metrics.length - 1 ? '1px solid #f1f5f9' : 'none',
            }}>
              <Typography variant="caption" sx={{
                color: '#94a3b8', fontSize: '0.52rem', display: 'block', lineHeight: 1,
                textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.4,
              }}>
                {m.label}
              </Typography>
              <Typography variant="caption" sx={{
                fontWeight: m.bold ? 800 : 700, fontFamily: 'monospace', fontSize: '0.82rem',
                color: m.color,
              }}>
                {m.value}
              </Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}

// ── Top Active Signals ───────────────────────────────────
function TopActiveSignals({ signals, t }) {
  if (!signals || signals.length === 0) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <SectionHeader title={t('legendary.accuracy.topActiveSignals', 'Top Active Signals')} color="#f59e0b"
        subtitle={`${signals.length} ${t('legendary.accuracy.activeSignalsSorted', 'active signals • sorted by score')}`} />
      <Card elevation={0} sx={{
        border: '1px solid', borderColor: alpha('#f59e0b', 0.12), borderRadius: 2, overflow: 'hidden',
      }}>
        <Box sx={{ height: 3, bgcolor: '#f59e0b' }} />
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.65rem', color: '#94a3b8', py: 1, textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '2px solid #f1f5f9' } }}>
                <TableCell>{t('legendary.accuracy.symbol', 'Symbol')}</TableCell>
                <TableCell>{t('legendary.accuracy.strategy', 'Strategy')}</TableCell>
                <TableCell align="center">Score</TableCell>
                <TableCell align="center">Strength</TableCell>
                <TableCell align="right">Entry</TableCell>
                <TableCell align="right">Target</TableCell>
                <TableCell align="right">Stop</TableCell>
                <TableCell align="center">R:R</TableCell>
                <TableCell>{t('legendary.accuracy.signalReasons', 'Signal Reasons')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {signals.map((s, idx) => {
                const color = STRATEGY_COLORS[s.strategy] || '#667eea';
                const rrColor = s.risk_reward >= 2 ? '#10b981' : s.risk_reward >= 1 ? '#3b82f6' : '#f59e0b';
                return (
                  <TableRow key={`${s.ticker}-${s.strategy || idx}`} sx={{
                    '& td': { fontSize: '0.78rem', py: 1, borderBottom: '1px solid #f8fafc' },
                    bgcolor: idx % 2 === 0 ? 'transparent' : '#fafbfd',
                    '&:hover': { bgcolor: alpha(color, 0.03) },
                  }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={700} sx={{ color: '#1e293b' }}>{s.ticker}</Typography>
                      <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.62rem' }}>{s.signal_date}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <DotIcon sx={{ fontSize: 8, color }} />
                        <Typography variant="caption" fontWeight={600} sx={{ color: '#475569', fontSize: '0.72rem' }}>
                          {STRATEGY_LABELS[s.strategy] || s.strategy}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight={700}
                        sx={{ color: s.score >= 90 ? '#10b981' : s.score >= 75 ? '#3b82f6' : '#f59e0b', fontFamily: 'monospace' }}>
                        {s.score?.toFixed(0)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {s.strength && (
                        <Chip size="small" icon={<StrengthIcon sx={{ fontSize: '12px !important' }} />}
                          label={s.strength}
                          sx={{
                            height: 20, fontSize: '0.6rem', fontWeight: 700,
                            bgcolor: s.strength === 'STRONG' ? alpha('#10b981', 0.08) : alpha('#f59e0b', 0.08),
                            color: s.strength === 'STRONG' ? '#10b981' : '#f59e0b',
                            borderRadius: 1,
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', color: '#475569' }}>
                      {s.entry_price?.toLocaleString() || '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', color: '#10b981' }}>
                      {s.target?.toLocaleString() || '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', color: '#ef4444' }}>
                      {s.stop?.toLocaleString() || '—'}
                    </TableCell>
                    <TableCell align="center">
                      {s.risk_reward != null && (
                        <Tooltip title="Risk/Reward ratio" placement="top">
                          <Box sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.3,
                            px: 0.8, py: 0.2, borderRadius: 1,
                            bgcolor: alpha(rrColor, 0.08),
                          }}>
                            <RRIcon sx={{ fontSize: 12, color: rrColor }} />
                            <Typography variant="caption" sx={{ fontWeight: 700, fontFamily: 'monospace', color: rrColor, fontSize: '0.72rem' }}>
                              {s.risk_reward}
                            </Typography>
                          </Box>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {(s.reasons || []).map((r, i) => {
                          const rs = getReasonChipStyle(r);
                          return (
                            <Chip key={i} size="small" label={reasonLabel(r, t)}
                              sx={{
                                height: 18, fontSize: '0.58rem', fontWeight: 600,
                                bgcolor: rs.bg, color: rs.color,
                                borderRadius: 0.8,
                              }}
                            />
                          );
                        })}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
}

// ── Chart wrapper ────────────────────────────────────────
function ChartCard({ title, children, empty, emptyMsg, accentColor, t }) {
  const accent = accentColor || '#6366f1';
  return (
    <Card elevation={0} sx={{
      border: '1px solid', borderColor: alpha(accent, 0.10), borderRadius: 2,
      flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0,
      overflow: 'hidden',
    }}>
      <Box sx={{
        px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: alpha('#e2e8f0', 0.6),
        bgcolor: alpha(accent, 0.03),
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 3, height: 16, borderRadius: 1, bgcolor: accent }} />
          <Typography variant="body2" fontWeight={700} sx={{ color: '#334155' }}>
            {title}
          </Typography>
        </Box>
      </Box>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
        {empty ? (
          <Box sx={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            flex: 1, minHeight: 240, color: '#cbd5e1', gap: 1,
          }}>
            <ChartIcon sx={{ fontSize: 40, opacity: 0.25 }} />
            <Typography variant="caption" sx={{ color: '#94a3b8', textAlign: 'center', maxWidth: 220 }}>
              {emptyMsg || t('legendary.accuracy.noData', 'No data available')}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ flex: 1, minHeight: 260 }}>
            {children}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

const chartTooltipStyle = {
  borderRadius: 8, border: '1px solid #e2e8f0',
  boxShadow: '0 2px 8px rgba(0,0,0,0.06)', fontSize: 12,
  padding: '8px 12px',
};

// ── Monthly Win Rate Chart ───────────────────────────────
function MonthlyChart({ data, t }) {
  if (!data || data.length === 0) return null;
  const hasWinRateData = data.some(d => d.win_rate != null);

  const monthMap = {};
  data.forEach(d => {
    if (!monthMap[d.month]) monthMap[d.month] = { month: d.month };
    monthMap[d.month][`${d.strategy}_wr`] = d.win_rate;
  });
  const chartData = Object.values(monthMap).sort((a, b) => a.month.localeCompare(b.month));

  return (
    <ChartCard title={t('legendary.accuracy.winRateByMonth', 'Win Rate by Month')} empty={!hasWinRateData}
      emptyMsg={t('legendary.accuracy.needTradingDays', 'Need a few trading days to calculate win rate. Auto-updates daily.')} t={t}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
          <XAxis dataKey="month" tick={{ fontSize: 11 }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} />
          <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} tickFormatter={v => `${v}%`} axisLine={false} tickLine={false} />
          <RechartsTooltip contentStyle={chartTooltipStyle}
            formatter={(value, name) => {
              const strat = name.replace('_wr', '');
              return [value != null ? `${value}%` : '—', STRATEGY_LABELS[strat] || strat];
            }}
          />
          <Legend formatter={v => STRATEGY_LABELS[v.replace('_wr', '')] || v} wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
          {Object.keys(STRATEGY_COLORS).map(strat => (
            <Line key={strat} type="monotone" dataKey={`${strat}_wr`}
              stroke={STRATEGY_COLORS[strat]} strokeWidth={2} dot={{ r: 3 }} connectNulls />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

// ── Monthly Signals Bar Chart ────────────────────────────
function MonthlySignalsChart({ data, t }) {
  if (!data || data.length === 0) return null;

  const monthMap = {};
  data.forEach(d => {
    if (!monthMap[d.month]) monthMap[d.month] = { month: d.month };
    monthMap[d.month][d.strategy] = d.signals;
  });
  const chartData = Object.values(monthMap).sort((a, b) => a.month.localeCompare(b.month));

  return (
    <ChartCard title={t('legendary.accuracy.signalsByMonth', 'Signals by Month')} t={t}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
          <XAxis dataKey="month" tick={{ fontSize: 11 }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} />
          <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
          <RechartsTooltip contentStyle={chartTooltipStyle}
            formatter={(value, name) => [value || 0, STRATEGY_LABELS[name] || name]}
          />
          <Legend formatter={v => STRATEGY_LABELS[v] || v} wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
          {Object.keys(STRATEGY_COLORS).map(strat => (
            <Bar key={strat} dataKey={strat} fill={STRATEGY_COLORS[strat]} radius={[2, 2, 0, 0]} stackId="a" />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

// ── Signal History Table ─────────────────────────────────
function SignalHistoryTable({ signals, total, page, pageSize, onPageChange, loading, t }) {
  const statusChip = (status, exitReason) => {
    const styles = { height: 22, fontSize: '0.68rem', fontWeight: 600, borderRadius: 1.5 };
    if (status === 'active') {
      return <Chip size="small" icon={<ActiveIcon sx={{ fontSize: '13px !important' }} />}
        label={t('legendary.accuracy.statusChip.active', 'Active')} sx={{ ...styles, bgcolor: alpha('#3b82f6', 0.06), color: '#3b82f6' }} />;
    }
    if (exitReason === 'target') {
      return <Chip size="small" icon={<WinIcon sx={{ fontSize: '13px !important' }} />}
        label={t('legendary.accuracy.statusChip.win', 'Win')} sx={{ ...styles, bgcolor: alpha('#10b981', 0.06), color: '#10b981' }} />;
    }
    if (exitReason === 'stop' || (typeof exitReason === 'string' && exitReason.startsWith('risk_stop'))) {
      return <Chip size="small" icon={<LossIcon sx={{ fontSize: '13px !important' }} />}
        label={t('legendary.accuracy.statusChip.loss', 'Loss')} sx={{ ...styles, bgcolor: alpha('#ef4444', 0.06), color: '#ef4444' }} />;
    }
    return <Chip size="small" icon={<ExpiredIcon sx={{ fontSize: '13px !important' }} />}
      label={t('legendary.accuracy.statusChip.expired', 'Expired')} sx={{ ...styles, bgcolor: alpha('#94a3b8', 0.06), color: '#94a3b8' }} />;
  };

  const stopModeChip = (mode) => {
    if (!mode) return '—';
    const v = String(mode).toLowerCase();
    if (v === 'regime') {
      return <Chip size="small" label={t('legendary.accuracy.stopMode.regime', 'Regime')} sx={{ height: 20, fontSize: '0.64rem', bgcolor: alpha('#0ea5e9', 0.08), color: '#0ea5e9' }} />;
    }
    if (v === 'single') {
      return <Chip size="small" label={t('legendary.accuracy.stopMode.single', 'Single')} sx={{ height: 20, fontSize: '0.64rem', bgcolor: alpha('#64748b', 0.1), color: '#64748b' }} />;
    }
    return <Chip size="small" label={mode} sx={{ height: 20, fontSize: '0.64rem' }} />;
  };

  const stopProfileChip = (profile) => {
    if (!profile) return '—';
    const v = String(profile).toLowerCase();
    if (v === 'defensive') {
      return <Chip size="small" label={t('legendary.accuracy.stopProfile.defensive', 'Defensive')} sx={{ height: 20, fontSize: '0.64rem', bgcolor: alpha('#ef4444', 0.08), color: '#ef4444' }} />;
    }
    if (v === 'balanced') {
      return <Chip size="small" label={t('legendary.accuracy.stopProfile.balanced', 'Balanced')} sx={{ height: 20, fontSize: '0.64rem', bgcolor: alpha('#10b981', 0.08), color: '#10b981' }} />;
    }
    return <Chip size="small" label={profile} sx={{ height: 20, fontSize: '0.64rem' }} />;
  };

  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      {loading && <LinearProgress sx={{ borderRadius: '8px 8px 0 0' }} />}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.65rem', color: '#94a3b8', py: 1.2, borderBottom: '2px solid #f1f5f9', textTransform: 'uppercase', letterSpacing: '0.04em' } }}>
              <TableCell>{t('legendary.accuracy.symbol', 'Symbol')}</TableCell>
              <TableCell>{t('legendary.accuracy.strategy', 'Strategy')}</TableCell>
              <TableCell align="center">{t('legendary.accuracy.col.score', 'Score')}</TableCell>
              <TableCell align="right">{t('legendary.accuracy.col.entry', 'Entry')}</TableCell>
              <TableCell align="right">{t('legendary.accuracy.col.stop', 'Stop')}</TableCell>
              <TableCell align="right">{t('legendary.accuracy.col.target', 'Target')}</TableCell>
              <TableCell align="center">{t('legendary.accuracy.col.rr', 'R:R')}</TableCell>
              <TableCell align="right">{t('legendary.accuracy.col.pnl', 'P&L')}</TableCell>
              <TableCell align="center">{t('legendary.accuracy.days', 'Days')}</TableCell>
              <TableCell align="center">{t('legendary.accuracy.col.stopMode', 'Stop Mode')}</TableCell>
              <TableCell align="center">{t('legendary.accuracy.col.stopProfile', 'Stop Profile')}</TableCell>
              <TableCell align="center">{t('legendary.accuracy.status', 'Status')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {signals.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={12} align="center" sx={{ py: 5, color: '#cbd5e1' }}>
                  {t('legendary.accuracy.noSignalData', 'No signal data')}
                </TableCell>
              </TableRow>
            )}
            {signals.map((s, idx) => {
              const color = STRATEGY_COLORS[s.strategy] || '#667eea';
              return (
                <TableRow key={s.id} sx={{
                  '& td': { fontSize: '0.78rem', py: 0.8, borderBottom: '1px solid #f8fafc' },
                  bgcolor: idx % 2 === 0 ? 'transparent' : '#fafbfd',
                  '&:hover': { bgcolor: alpha(color, 0.03) },
                }}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={700} sx={{ color: '#1e293b' }}>{s.ticker}</Typography>
                    <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.6rem', fontFamily: 'monospace' }}>{s.signal_date}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <DotIcon sx={{ fontSize: 7, color }} />
                      <Typography variant="caption" fontWeight={600} sx={{ color: '#475569', fontSize: '0.72rem' }}>
                        {STRATEGY_LABELS[s.strategy] || s.strategy}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight={700} sx={{
                      color: s.score >= 90 ? '#10b981' : s.score >= 75 ? '#3b82f6' : '#f59e0b',
                      fontFamily: 'monospace',
                    }}>
                      {s.score?.toFixed(0)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'monospace', color: '#475569', fontSize: '0.72rem' }}>
                    {s.entry_price ? s.entry_price.toLocaleString() : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'monospace', color: '#ef4444', fontSize: '0.72rem' }}>
                    {s.stop ? s.stop.toLocaleString() : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'monospace', color: '#10b981', fontSize: '0.72rem' }}>
                    {s.target ? s.target.toLocaleString() : '—'}
                  </TableCell>
                  <TableCell align="center">
                    {s.risk_reward != null ? (
                      <Typography variant="caption" sx={{
                        fontWeight: 700, fontFamily: 'monospace', fontSize: '0.72rem',
                        color: s.risk_reward >= 1.5 ? '#10b981' : '#64748b',
                      }}>
                        {s.risk_reward}
                      </Typography>
                    ) : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{
                    fontFamily: 'monospace', fontWeight: 700,
                    color: s.pct_change > 0 ? '#10b981' : s.pct_change < 0 ? '#ef4444' : '#cbd5e1',
                  }}>
                    {s.pct_change != null ? `${s.pct_change > 0 ? '+' : ''}${s.pct_change.toFixed(2)}%` : '—'}
                  </TableCell>
                  <TableCell align="center" sx={{ color: '#94a3b8', fontFamily: 'monospace', fontSize: '0.72rem' }}>
                    {s.days_held || '—'}
                  </TableCell>
                  <TableCell align="center">{stopModeChip(s.stop_mode)}</TableCell>
                  <TableCell align="center">{stopProfileChip(s.stop_profile)}</TableCell>
                  <TableCell align="center">
                    {statusChip(s.status, s.exit_reason)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {total > pageSize && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5, borderTop: '1px solid #f1f5f9' }}>
          <Pagination count={Math.ceil(total / pageSize)} page={page}
            onChange={(_, v) => onPageChange(v)} size="small" color="primary" />
        </Box>
      )}
    </Card>
  );
}

// ── Main AccuracyHistory Component ───────────────────────
export default function AccuracyHistory() {
  const { t } = useTranslation();
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState(null);
  const [signals, setSignals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signalsLoading, setSignalsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filters
  const [strategyFilter, setStrategyFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [stopModeFilter, setStopModeFilter] = useState('');
  const [stopProfileFilter, setStopProfileFilter] = useState('');
  const [daysFilter, setDaysFilter] = useState(90);
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const fetchSummaryAndHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [summaryRes, historyRes] = await Promise.all([
        legendaryAPI.getAccuracySummary(),
        legendaryAPI.getAccuracyHistory({ months: 12 }),
      ]);
      setSummary(summaryRes.data);
      setHistory(historyRes.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSignals = useCallback(async () => {
    try {
      setSignalsLoading(true);
      const res = await legendaryAPI.getSignalHistory({
        strategy: strategyFilter || undefined,
        status: statusFilter || undefined,
        stopMode: stopModeFilter || undefined,
        stopProfile: stopProfileFilter || undefined,
        days: daysFilter,
        page,
        pageSize,
      });
      setSignals(res.data);
    } catch (err) {
      console.error('Signals error:', err);
    } finally {
      setSignalsLoading(false);
    }
  }, [strategyFilter, statusFilter, stopModeFilter, stopProfileFilter, daysFilter, page]);

  useEffect(() => { fetchSummaryAndHistory(); }, [fetchSummaryAndHistory]);
  useEffect(() => { fetchSignals(); }, [fetchSignals]);

  if (loading) {
    return (
      <Box sx={{ width: '100%' }}>
        {/* ── Overview Banner skeleton (5 MetricCards) ──── */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, width: '100%' }}>
          {[
            { w: 60, c: '#6366f1' },
            { w: 48, c: '#10b981' },
            { w: 56, c: '#f59e0b' },
            { w: 44, c: '#0ea5e9' },
            { w: 36, c: '#8b5cf6' },
          ].map((m, i) => (
            <Card key={i} elevation={0} sx={{
              flex: 1, minWidth: 0, borderRadius: 2, overflow: 'hidden',
              border: '1px solid', borderColor: alpha(m.c, 0.12),
            }}>
              <Box sx={{ height: 3, bgcolor: alpha(m.c, 0.3) }} />
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <Box>
                    <Skeleton width={m.w} height={10} sx={{ mb: 0.8, borderRadius: 0.5 }} />
                    <Skeleton width={m.w - 10} height={22} sx={{ borderRadius: 1 }} />
                    <Skeleton width={m.w - 20} height={9} sx={{ mt: 0.5, borderRadius: 0.5 }} />
                  </Box>
                  <Skeleton variant="rounded" width={36} height={36} sx={{ borderRadius: 2, flexShrink: 0 }} />
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* ── Section Header: Strategies ──── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Box sx={{ width: 4, height: 24, borderRadius: 2, bgcolor: alpha('#8b5cf6', 0.3) }} />
          <Box>
            <Skeleton width={80} height={16} sx={{ borderRadius: 0.5 }} />
            <Skeleton width={140} height={10} sx={{ mt: 0.3, borderRadius: 0.5 }} />
          </Box>
        </Box>

        {/* ── Strategy Cards skeleton (3 cards) ──── */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
          {[
            { c: '#6366f1' },
            { c: '#f59e0b' },
            { c: '#f43f5e' },
          ].map((s, i) => (
            <Card key={i} elevation={0} sx={{
              borderRadius: 2, flex: '1 1 0', minWidth: 260, position: 'relative', overflow: 'hidden',
              border: '1px solid', borderColor: alpha(s.c, 0.10),
            }}>
              <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, bgcolor: alpha(s.c, 0.3) }} />
              <CardContent sx={{ p: 0, pl: 0.5, '&:last-child': { pb: 0 } }}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 1.5, pb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Skeleton variant="rounded" width={28} height={28} sx={{ borderRadius: 1.5 }} />
                    <Skeleton width={70} height={16} sx={{ borderRadius: 0.5 }} />
                  </Box>
                  <Skeleton width={52} height={22} sx={{ borderRadius: 1.5 }} />
                </Box>
                {/* Description */}
                <Box sx={{ px: 2, mb: 1 }}>
                  <Skeleton width="80%" height={10} sx={{ borderRadius: 0.5 }} />
                </Box>
                {/* Progress bar */}
                <Box sx={{ px: 2, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Skeleton variant="rounded" width="100%" height={5} sx={{ borderRadius: 3, flex: 1 }} />
                  <Skeleton width={30} height={8} sx={{ borderRadius: 0.5, flexShrink: 0 }} />
                </Box>
                {/* Metrics row */}
                <Box sx={{ display: 'flex', borderTop: '1px solid #f1f5f9' }}>
                  {[0, 1, 2, 3].map((j) => (
                    <Box key={j} sx={{ flex: 1, textAlign: 'center', py: 1, borderRight: j < 3 ? '1px solid #f1f5f9' : 'none' }}>
                      <Skeleton width={36} height={8} sx={{ mx: 'auto', mb: 0.4, borderRadius: 0.5 }} />
                      <Skeleton width={28} height={14} sx={{ mx: 'auto', borderRadius: 0.5 }} />
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* ── Section Header: Top signals ──── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Box sx={{ width: 4, height: 24, borderRadius: 2, bgcolor: alpha('#f59e0b', 0.3) }} />
          <Box>
            <Skeleton width={160} height={16} sx={{ borderRadius: 0.5 }} />
            <Skeleton width={200} height={10} sx={{ mt: 0.3, borderRadius: 0.5 }} />
          </Box>
        </Box>

        {/* ── Top Active Signals table skeleton ──── */}
        <Card elevation={0} sx={{
          border: '1px solid', borderColor: alpha('#f59e0b', 0.10), borderRadius: 2, overflow: 'hidden', mb: 3,
        }}>
          <Box sx={{ height: 3, bgcolor: alpha('#f59e0b', 0.3) }} />
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { py: 1, borderBottom: '2px solid #f1f5f9' } }}>
                {['Symbol', 'Strategy', 'Score', 'Strength', 'Entry', 'Target', 'Stop', 'R:R', 'Reasons'].map(h => (
                  <TableCell key={h}>
                    <Skeleton width={h === 'Reasons' ? 60 : h.length * 8 + 10} height={10} sx={{ borderRadius: 0.5 }} />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {[0, 1, 2, 3, 4].map(r => (
                <TableRow key={r} sx={{ bgcolor: r % 2 === 1 ? '#fafbfd' : 'transparent', '& td': { py: 1, borderBottom: '1px solid #f8fafc' } }}>
                  <TableCell>
                    <Skeleton width={36} height={14} sx={{ mb: 0.2 }} />
                    <Skeleton width={56} height={8} />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Skeleton variant="circular" width={8} height={8} />
                      <Skeleton width={52} height={12} />
                    </Box>
                  </TableCell>
                  <TableCell align="center"><Skeleton width={28} height={14} sx={{ mx: 'auto' }} /></TableCell>
                  <TableCell align="center"><Skeleton width={50} height={18} sx={{ mx: 'auto', borderRadius: 1 }} /></TableCell>
                  <TableCell align="right"><Skeleton width={48} height={12} sx={{ ml: 'auto' }} /></TableCell>
                  <TableCell align="right"><Skeleton width={48} height={12} sx={{ ml: 'auto' }} /></TableCell>
                  <TableCell align="right"><Skeleton width={48} height={12} sx={{ ml: 'auto' }} /></TableCell>
                  <TableCell align="center"><Skeleton width={36} height={18} sx={{ mx: 'auto', borderRadius: 1 }} /></TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Skeleton width={56} height={18} sx={{ borderRadius: 2 }} />
                      <Skeleton width={48} height={18} sx={{ borderRadius: 2 }} />
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        {/* ── Charts skeleton ──── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Box sx={{ width: 4, height: 24, borderRadius: 2, bgcolor: alpha('#0ea5e9', 0.3) }} />
          <Skeleton width={120} height={16} sx={{ borderRadius: 0.5 }} />
        </Box>
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          {[0, 1].map(i => (
            <Card key={i} elevation={0} sx={{
              flex: 1, borderRadius: 2, overflow: 'hidden',
              border: '1px solid', borderColor: '#f1f5f9',
            }}>
              <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                <Skeleton width={100} height={14} sx={{ mb: 0.5 }} />
                <Skeleton width={150} height={10} />
              </Box>
              <Box sx={{ px: 2, pb: 2, display: 'flex', alignItems: 'flex-end', gap: 0.8, height: 160 }}>
                {[40, 65, 50, 80, 55, 70, 45, 60, 75, 50, 85, 55].map((h, j) => (
                  <Skeleton key={j} variant="rounded" width="100%" height={`${h}%`} sx={{ borderRadius: 0.5, flex: 1 }} />
                ))}
              </Box>
            </Card>
          ))}
        </Box>

        {/* ── Filters + Table skeleton ──── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Box sx={{ width: 4, height: 24, borderRadius: 2, bgcolor: alpha('#64748b', 0.3) }} />
          <Box>
            <Skeleton width={100} height={16} sx={{ borderRadius: 0.5 }} />
            <Skeleton width={70} height={10} sx={{ mt: 0.3, borderRadius: 0.5 }} />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
          <Skeleton variant="rounded" width={130} height={36} sx={{ borderRadius: 2 }} />
          <Skeleton variant="rounded" width={110} height={36} sx={{ borderRadius: 2 }} />
          <Skeleton variant="rounded" width={110} height={36} sx={{ borderRadius: 2 }} />
        </Box>
        <Card elevation={0} sx={{ border: '1px solid', borderColor: '#f1f5f9', borderRadius: 2, overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { py: 1, borderBottom: '2px solid #f1f5f9' } }}>
                {[60, 50, 36, 44, 44, 44, 36, 44, 36, 80].map((w, i) => (
                  <TableCell key={i}><Skeleton width={w} height={10} sx={{ borderRadius: 0.5 }} /></TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {[0, 1, 2, 3, 4].map(r => (
                <TableRow key={r} sx={{ bgcolor: r % 2 === 1 ? '#fafbfd' : 'transparent', '& td': { py: 1, borderBottom: '1px solid #f8fafc' } }}>
                  {[36, 52, 28, 40, 40, 40, 28, 36, 28, 60].map((w, j) => (
                    <TableCell key={j}><Skeleton width={w} height={12} /></TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>;
  }

  const activeStrategies = summary?.strategies?.filter(s => s.total_signals > 0) || [];

  return (
    <Box sx={{ width: '100%' }}>
      {/* ── Overview Banner — reliability summary ─────── */}
      <OverviewBanner summary={summary} t={t} />

      {/* ── Signal History (PRIMARY) ──────────────────── */}
      <Box sx={{ mb: 3 }}>
        <SectionHeader
          title={t('legendary.accuracy.allSignals', 'Signal History')}
          subtitle={t('legendary.accuracy.allSignalsSubtitle', 'Track every signal — evaluate reliability over time')
            + ` • ${signals?.total || 0} ${t('legendary.accuracy.results', 'results')}`}
          color="#6366f1"
        />

        <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel sx={{ fontSize: '0.82rem' }}>{t('legendary.accuracy.strategy', 'Strategy')}</InputLabel>
            <Select value={strategyFilter} label={t('legendary.accuracy.strategy', 'Strategy')}
              onChange={e => { setStrategyFilter(e.target.value); setPage(1); }}
              sx={{ borderRadius: 2, fontSize: '0.82rem' }}>
              <MenuItem value="">{t('legendary.accuracy.all', 'All')}</MenuItem>
              {Object.entries(STRATEGY_LABELS).map(([k, v]) => (
                <MenuItem key={k} value={k}>{v}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel sx={{ fontSize: '0.82rem' }}>{t('legendary.accuracy.status', 'Status')}</InputLabel>
            <Select value={statusFilter} label={t('legendary.accuracy.status', 'Status')}
              onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
              sx={{ borderRadius: 2, fontSize: '0.82rem' }}>
              <MenuItem value="">{t('legendary.accuracy.all', 'All')}</MenuItem>
              <MenuItem value="active">{t('legendary.accuracy.statusOption.active', 'Active')}</MenuItem>
              <MenuItem value="closed">{t('legendary.accuracy.statusOption.closed', 'Closed')}</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel sx={{ fontSize: '0.82rem' }}>{t('legendary.accuracy.period', 'Period')}</InputLabel>
            <Select value={daysFilter} label={t('legendary.accuracy.period', 'Period')}
              onChange={e => { setDaysFilter(e.target.value); setPage(1); }}
              sx={{ borderRadius: 2, fontSize: '0.82rem' }}>
              <MenuItem value={30}>{t('legendary.accuracy.days30', '30 days')}</MenuItem>
              <MenuItem value={60}>{t('legendary.accuracy.days60', '60 days')}</MenuItem>
              <MenuItem value={90}>{t('legendary.accuracy.days90', '90 days')}</MenuItem>
              <MenuItem value={180}>{t('legendary.accuracy.months6', '6 months')}</MenuItem>
              <MenuItem value={365}>{t('legendary.accuracy.year1', '1 year')}</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel sx={{ fontSize: '0.82rem' }}>{t('legendary.accuracy.stopMode.label', 'Stop Mode')}</InputLabel>
            <Select value={stopModeFilter} label={t('legendary.accuracy.stopMode.label', 'Stop Mode')}
              onChange={e => { setStopModeFilter(e.target.value); setPage(1); }}
              sx={{ borderRadius: 2, fontSize: '0.82rem' }}>
              <MenuItem value="">{t('legendary.accuracy.all', 'All')}</MenuItem>
              <MenuItem value="regime">{t('legendary.accuracy.stopMode.regime', 'Regime')}</MenuItem>
              <MenuItem value="single">{t('legendary.accuracy.stopMode.single', 'Single')}</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel sx={{ fontSize: '0.82rem' }}>{t('legendary.accuracy.stopProfile.label', 'Stop Profile')}</InputLabel>
            <Select value={stopProfileFilter} label={t('legendary.accuracy.stopProfile.label', 'Stop Profile')}
              onChange={e => { setStopProfileFilter(e.target.value); setPage(1); }}
              sx={{ borderRadius: 2, fontSize: '0.82rem' }}>
              <MenuItem value="">{t('legendary.accuracy.all', 'All')}</MenuItem>
              <MenuItem value="balanced">{t('legendary.accuracy.stopProfile.balanced', 'Balanced')}</MenuItem>
              <MenuItem value="defensive">{t('legendary.accuracy.stopProfile.defensive', 'Defensive')}</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <SignalHistoryTable
          signals={signals?.signals || []}
          total={signals?.total || 0}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          loading={signalsLoading}
          t={t}
        />
      </Box>

      {/* ── Supporting Analytics divider ──────────────── */}
      {(activeStrategies.length > 0 || (summary?.top_active_signals?.length > 0) || (history?.months?.length > 0)) && (
        <Box sx={{ mb: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 4, height: 24, borderRadius: 2, bgcolor: alpha('#94a3b8', 0.5) }} />
          <Box>
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1e293b', fontSize: '0.95rem', lineHeight: 1.3 }}>
              {t('legendary.accuracy.supportingAnalytics', 'Supporting Analytics')}
            </Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.72rem' }}>
              {t('legendary.accuracy.supportingAnalyticsSub', 'Per-strategy, active signals, monthly charts')}
            </Typography>
          </Box>
        </Box>
      )}

      {/* ── Per-Strategy Cards ────────────────────────── */}
      {activeStrategies.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <SectionHeader title={t('legendary.accuracy.strategies', 'Strategies')} color="#8b5cf6"
            subtitle={`${activeStrategies.length} ${t('legendary.accuracy.activeStrategies', 'active strategies')}`} />
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {activeStrategies.map(s => (
              <StrategyCard key={s.strategy} data={s} />
            ))}
          </Box>
        </Box>
      )}

      {/* ── Top Active Signals ────────────────────────── */}
      <TopActiveSignals signals={summary?.top_active_signals} t={t} />

      {/* ── Charts ────────────────────────────────────── */}
      {history?.months?.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <SectionHeader title={t('legendary.accuracy.chartsByMonth', 'Charts by Month')} color="#0ea5e9" />
          <Box sx={{ display: 'flex', gap: 2, width: '100%', alignItems: 'stretch' }}>
            <MonthlyChart data={history.months} t={t} />
            <MonthlySignalsChart data={history.months} t={t} />
          </Box>
        </Box>
      )}

      {/* ── No data state ─────────────────────────────── */}
      {summary?.total_signals === 0 && (
        <Alert severity="info" sx={{ borderRadius: 2, width: '100%' }}>
          {t('legendary.accuracy.noTrackingData', 'No tracking data yet. The system will start tracking signals daily.')}
        </Alert>
      )}
    </Box>
  );
}
